import React from 'react';
import { observer } from 'mobx-react';
import { Grid, Divider } from '@mui/material';
import { LevelStore } from '@gamiphy/loyalty-station-desktop/store/level';
import { useDisplayStringValue, Trans } from '@gamiphy/react-i18n';
import { TierColors } from '@gamiphy/bl/level';
import Color from 'color';
import { ProgressDescription } from '@gamiphy/loyalty/src/levels/components';

type Props = {
  progress: number,
}

const CustomCircularProgress: React.FC<Props> = observer(({ progress }) => {
  const circularProgress = React.useMemo(() => progress * 360, [progress]);

  const levelName = useDisplayStringValue(LevelStore.currentTier?.name);

  const levelNumber = React.useMemo(() => {
    switch (true) {
      case !LevelStore.nextTier || LevelStore.tiers?.length === 1:
        return levelName;
      default:
        return (LevelStore.currentTier?.order ?? 0) + 1;
    }
  }, [LevelStore.nextTier, LevelStore.tiers, LevelStore.currentTier, levelName]);

  return (
    <Grid
      sx={{
        width: '100%',
        borderRadius: '8px',
      }}
      container
      direction="column"
      alignItems="center"
    >
      <Grid
        sx={{
          position: 'relative',
          height: 160,
          width: 160,
          borderRadius: '50%',
          background: `conic-gradient(${LevelStore.currentTier?.color ?? TierColors.bronze} ${circularProgress}deg, ${Color(LevelStore.currentTier?.color ?? TierColors.bronze).hex().toString()}80 0deg)`,
          '&::before': {
            content: '""',
            position: 'absolute',
            height: 145,
            width: 145,
            backgroundColor: '#fff',
            borderRadius: '50%',
          },
        }}
        container
        alignItems="center"
        justifyContent="center"
      >
        <Grid
          sx={{
            zIndex: 0, paddingInline: 2, fontSize: '12px', fontWeight: 600,
          }}
          container
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={12}>
            <Trans
              i18nKey="tier.number"
              defaults="Level {{number}}"
              values={{
                number: levelNumber,
              }}
            />
          </Grid>
          {!(!LevelStore.nextTier || LevelStore.tiers?.length === 1) &&
          (
          <Grid item xs={12}>
            {levelName}
          </Grid>
          )}
          <Divider sx={{ width: '90%', marginY: '0.5rem' }} />
          <Grid sx={{
            overflow: 'hidden',
            fontSize: 10,
            paddingInline: 1,
          }}
          >
            <ProgressDescription
              accumulated={LevelStore.accumulated}
              nextTier={LevelStore.nextTier}
              tierConfig={LevelStore.tierConfig}
              tiers={LevelStore.tiers}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
});

export default CustomCircularProgress;
